import { Injectable, Inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { environment } from '@environments/environment';
import { PlatformService } from '@core/services/platform.service';
import { MobileOptimizationService } from '../shared/services/mobile-optimization.service';
import has from 'lodash/has';
import { extractWhiteLabelConfig, getPortfolioTypeTerms } from '@utils/common.utils';
import {
  SeoMetadata,
  StructuredData,
  OpenGraphData,
  TwitterCardData,
  BreadcrumbItem,
  ArticleData,
  PersonData,
  OrganizationData,
  SeoValidationResult,
  VideoData
} from '@models/seo.interface';

interface SubCategory {
  pk: string;
  sk: string;
  category_name: string;
  category_id: string;
  tempCategory_name?: string;
  mainCategory?: string;
  [key: string]: any;
}
import { Application } from '@enums/application.enums';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly defaultImageUrl = 'https://cdn-www.topfans.com/topfans.png';
  private readonly defaultSiteName = 'TopFan';
  private readonly defaultTwitterSite = '@topfans';
  private readonly defaultLocale = 'en_US';
  
  // Performance optimization constants
  private readonly MAX_DESCRIPTION_LENGTH = 160;
  private readonly MAX_TITLE_LENGTH = 60;
  private readonly MAX_KEYWORDS_LENGTH = 255;
  private readonly OG_IMAGE_WIDTH = 1200;
  private readonly OG_IMAGE_HEIGHT = 630;
  private readonly OG_IMAGE_QUALITY = 80;
  
  // Cache for frequently used data
  private readonly cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  
  // Batch DOM updates for better performance
  private updateQueue: Array<() => void> = [];
  private updateScheduled = false;

  constructor(
    private meta: Meta,
    private titleService: Title,
    private platformService: PlatformService,
    private mobileOptimizationService: MobileOptimizationService,
    @Inject(DOCUMENT) private document: Document,
  ) {
  }

  /**
   * Set comprehensive SEO metadata for a page
   * @param metadata - SEO metadata object
   */
  setSeoMetadata(metadata: SeoMetadata): void {
    try {
      // Validate input
      if (!metadata || typeof metadata !== 'object') {
        console.warn('Invalid SEO metadata provided');
        return;
      }

      if (!metadata.title || typeof metadata.title !== 'string') {
        console.warn('Title is required and must be a string');
        return;
      }

      if (!metadata.description || typeof metadata.description !== 'string') {
        console.warn('Description is required and must be a string');
        return;
      }

      // Clean and validate data
      const cleanMetadata = {
        ...metadata,
        title: this.cleanText(metadata.title),
        description: this.cleanText(metadata.description),
        keywords: metadata.keywords ? this.cleanText(metadata.keywords) : undefined,
        author: metadata.author ? this.cleanText(metadata.author) : undefined
      };

      // Batch DOM updates for better performance
      this.batchUpdate(() => {
        this.setTitle(cleanMetadata.title);
        this.setDescription(cleanMetadata.description);

        if (cleanMetadata.keywords) {
          this.setKeywords(cleanMetadata.keywords);
        }

        if (cleanMetadata.author) {
          this.setAuthor(cleanMetadata.author);
        }

        // Note: Canonical URLs are handled by CanonicalService in app.component.ts
        // on every NavigationEnd event. No need to set it here.

        this.setOpenGraphTags({
          title: cleanMetadata.title,
          description: cleanMetadata.description,
          imageUrl: cleanMetadata.imageUrl || this.defaultImageUrl,
          url: cleanMetadata.canonicalUrl || this.getCurrentUrl(),
          type: cleanMetadata.ogType || 'website',
          siteName: cleanMetadata.siteName || this.defaultSiteName,
          locale: cleanMetadata.locale || this.defaultLocale,
          publishedTime: cleanMetadata.publishedTime,
          modifiedTime: cleanMetadata.modifiedTime,
          section: cleanMetadata.section,
          tags: cleanMetadata.tags,
          videoUrl: cleanMetadata.videoUrl,
          videoType: cleanMetadata.videoType,
          videoWidth: cleanMetadata.videoWidth,
          videoHeight: cleanMetadata.videoHeight
        });

        // Build Twitter Card data with video player support
        const twitterCardData: any = {
          title: cleanMetadata.title,
          description: cleanMetadata.description,
          imageUrl: cleanMetadata.imageUrl || this.defaultImageUrl,
          url: cleanMetadata.canonicalUrl || this.getCurrentUrl(),
          card: cleanMetadata.twitterCard || 'summary_large_image',
          site: this.defaultTwitterSite,
          creator: cleanMetadata.twitterHandle
        };

        // Add Twitter Player tags for video content
        if (cleanMetadata.ogType === 'video' && cleanMetadata.videoUrl) {
          twitterCardData.card = 'player';
          twitterCardData.player = cleanMetadata.canonicalUrl || this.getCurrentUrl();
          twitterCardData.playerWidth = cleanMetadata.videoWidth || 1280;
          twitterCardData.playerHeight = cleanMetadata.videoHeight || 720;
          twitterCardData.playerStream = cleanMetadata.videoUrl;
          twitterCardData.playerStreamContentType = cleanMetadata.videoType || 'application/x-mpegURL';
        }

        this.setTwitterTags(twitterCardData);

      });
    } catch (error) {
      console.error('Error setting SEO metadata:', error);
    }
  }

  /**
   * Set page title with validation and optimization
   * @param title - Page title
   */
  setTitle(title: string): void {
    if (!title || typeof title !== 'string') {
      console.warn('Invalid title provided to setTitle');
      return;
    }
    
    const cleanTitle = this.cleanText(title);
    const optimizedTitle = cleanTitle.length > this.MAX_TITLE_LENGTH 
      ? cleanTitle.substring(0, this.MAX_TITLE_LENGTH - 3) + '...'
      : cleanTitle;
    
    this.titleService.setTitle(optimizedTitle);
  }

  /**
   * Set meta description with validation and optimization
   * @param description - Page description
   */
  setDescription(description: string): void {
    if (!description || typeof description !== 'string') {
      console.warn('Invalid description provided to setDescription');
      return;
    }
    
    const cleanDescription = this.cleanText(description);
    const optimizedDescription = cleanDescription.length > this.MAX_DESCRIPTION_LENGTH
      ? cleanDescription.substring(0, this.MAX_DESCRIPTION_LENGTH - 3) + '...'
      : cleanDescription;
    
    this.meta.updateTag({ name: 'description', content: optimizedDescription });
  }

  /**
   * Set meta keywords
   * @param keywords - Comma-separated keywords
   */
  setKeywords(keywords: string): void {
    this.meta.updateTag({ name: 'keywords', content: keywords });
  }

  /**
   * Set meta author
   * @param author - Author name
   */
  setAuthor(author: string): void {
    this.meta.updateTag({ name: 'author', content: author });
  }

  /**
   * Set Open Graph tags
   * @param ogData - Open Graph data
   */
  setOpenGraphTags(ogData: OpenGraphData): void {
    const cleanDescription = this.cleanText(ogData.description);
    const optimizedImageUrl = this.mobileOptimizationService.getOptimizedImageUrl(ogData.imageUrl, this.OG_IMAGE_WIDTH, this.OG_IMAGE_HEIGHT, this.OG_IMAGE_QUALITY, 'webp');

    this.meta.updateTag({ property: 'og:title', content: ogData.title });
    this.meta.updateTag({ property: 'og:description', content: cleanDescription });
    this.meta.updateTag({ property: 'og:image', content: optimizedImageUrl });
    this.meta.updateTag({ property: 'og:url', content: ogData.url });
    this.meta.updateTag({ property: 'og:type', content: ogData.type || 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: ogData.siteName || this.defaultSiteName });
    this.meta.updateTag({ property: 'og:locale', content: ogData.locale || this.defaultLocale });
    
    // Handle video tags for m3u8 (HLS) format
    if (ogData.videoUrl && ogData.type === 'video') {
      this.meta.updateTag({ property: 'og:video', content: ogData.videoUrl });
      this.meta.updateTag({ property: 'og:video:secure_url', content: ogData.videoUrl });
      // Add player_loc URL for video sitemap compliance
      this.meta.updateTag({ property: 'og:video:url', content: ogData.url });

      // Set video type based on format (m3u8 = HLS)
      const videoType = ogData.videoType || (ogData.videoUrl.includes('.m3u8') ? 'application/x-mpegURL' : 'video/mp4');
      this.meta.updateTag({ property: 'og:video:type', content: videoType });

      // Add video dimensions if available
      if (ogData.videoWidth) {
        this.meta.updateTag({ property: 'og:video:width', content: ogData.videoWidth.toString() });
      } else {
        // Default dimensions for video player
        this.meta.updateTag({ property: 'og:video:width', content: '1280' });
      }
      if (ogData.videoHeight) {
        this.meta.updateTag({ property: 'og:video:height', content: ogData.videoHeight.toString() });
      } else {
        // Default dimensions for video player
        this.meta.updateTag({ property: 'og:video:height', content: '720' });
      }
    }
    
    if (ogData.publishedTime) {
      this.meta.updateTag({ property: 'article:published_time', content: ogData.publishedTime });
    }
    
    if (ogData.modifiedTime) {
      this.meta.updateTag({ property: 'article:modified_time', content: ogData.modifiedTime });
    }
    
    if (ogData.section) {
      this.meta.updateTag({ property: 'article:section', content: ogData.section });
    }
    
    if (ogData.tags && ogData.tags.length > 0) {
      ogData.tags.forEach(tag => {
        this.meta.addTag({ property: 'article:tag', content: tag });
      });
    }
  }

  /**
   * Set Twitter Card tags
   * @param twitterData - Twitter Card data
   */
  setTwitterTags(twitterData: TwitterCardData): void {
    const cleanDescription = this.cleanText(twitterData.description);

    this.meta.updateTag({ name: 'twitter:card', content: twitterData.card || 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: twitterData.title });
    this.meta.updateTag({ name: 'twitter:description', content: cleanDescription });
    this.meta.updateTag({ name: 'twitter:image', content: twitterData.imageUrl });
    this.meta.updateTag({ name: 'twitter:url', content: twitterData.url });

    if (twitterData.site) {
      this.meta.updateTag({ name: 'twitter:site', content: twitterData.site });
    }

    if (twitterData.creator) {
      this.meta.updateTag({ name: 'twitter:creator', content: twitterData.creator });
    }

    // Add Twitter Player tags for video content
    if (twitterData.player) {
      this.meta.updateTag({ name: 'twitter:player', content: twitterData.player });

      if (twitterData.playerWidth) {
        this.meta.updateTag({ name: 'twitter:player:width', content: twitterData.playerWidth.toString() });
      }

      if (twitterData.playerHeight) {
        this.meta.updateTag({ name: 'twitter:player:height', content: twitterData.playerHeight.toString() });
      }

      if (twitterData.playerStream) {
        this.meta.updateTag({ name: 'twitter:player:stream', content: twitterData.playerStream });
      }

      if (twitterData.playerStreamContentType) {
        this.meta.updateTag({ name: 'twitter:player:stream:content_type', content: twitterData.playerStreamContentType });
      }
    }
  }


  /**
   * Add structured data (JSON-LD) to the page
   * @param structuredData - Structured data object
   */
  addStructuredData(structuredData: StructuredData): void {
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    this.document.head.appendChild(script);
  }

  /**
   * Remove all structured data from the page
   */
  removeAllStructuredData(): void {
    const scripts = this.document.querySelectorAll('script[type="application/ld+json"]');
    scripts.forEach(script => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    });
  }

  /**
   * Set robots meta tag
   * @param content - Robots content (e.g., 'index, follow', 'noindex, nofollow')
   */
  setRobots(content: string): void {
    this.meta.updateTag({ name: 'robots', content: content });
  }

  /**
   * Set viewport meta tag
   * @param content - Viewport content
   */
  setViewport(content: string): void {
    this.meta.updateTag({ name: 'viewport', content: content });
  }

  /**
   * Set language meta tag
   * @param language - Language code (e.g., 'en', 'es', 'fr')
   */
  setLanguage(language: string): void {
    this.meta.updateTag({ name: 'language', content: language });
  }

  /**
   * Set theme color meta tag
   * @param color - Theme color (hex code)
   */
  setThemeColor(color: string): void {
    this.meta.updateTag({ name: 'theme-color', content: color });
  }

  /**
   * Generate breadcrumb structured data
   * @param breadcrumbs - Array of breadcrumb items
   */
  addBreadcrumbStructuredData(breadcrumbs: BreadcrumbItem[]): void {
    const structuredData: StructuredData = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((breadcrumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: breadcrumb.name,
        item: breadcrumb.url
      }))
    };
    
    this.addStructuredData(structuredData);
  }

  /**
   * Generate article structured data
   * @param articleData - Article data
   */
  addArticleStructuredData(articleData: ArticleData): void {
    const structuredData: StructuredData = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: articleData.headline,
      description: this.cleanText(articleData.description),
      image: articleData.imageUrl,
      author: {
        '@type': 'Person',
        name: articleData.author
      },
      publisher: {
        '@type': 'Organization',
        name: this.defaultSiteName,
        logo: {
          '@type': 'ImageObject',
          url: this.defaultImageUrl
        }
      },
      datePublished: articleData.publishedDate,
      dateModified: articleData.modifiedDate || articleData.publishedDate,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': articleData.url
      }
    };
    
    this.addStructuredData(structuredData);
  }

  /**
   * Generate video structured data (VideoObject)
   * @param videoData - Video data
   */
  addVideoStructuredData(videoData: VideoData): void {
    const structuredData: StructuredData = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: videoData.name,
      description: this.cleanText(videoData.description),
      thumbnailUrl: videoData.thumbnailUrl,
      contentUrl: videoData.contentUrl,
      uploadDate: videoData.uploadDate,
      url: videoData.url,
      author: {
        '@type': 'Person',
        name: videoData.author
      }
    };

    // Add embed URL if available
    if (videoData.embedUrl) {
      structuredData.embedUrl = videoData.embedUrl;
    }

    // Add duration if available (ISO 8601 format: PT1M30S for 1 minute 30 seconds)
    if (videoData.duration) {
      structuredData.duration = videoData.duration;
    }

    // Add publisher if available
    if (videoData.publisher) {
      structuredData.publisher = {
        '@type': 'Organization',
        name: videoData.publisher,
        logo: {
          '@type': 'ImageObject',
          url: this.defaultImageUrl
        }
      };
    } else {
      // Default publisher
      structuredData.publisher = {
        '@type': 'Organization',
        name: this.defaultSiteName,
        logo: {
          '@type': 'ImageObject',
          url: this.defaultImageUrl
        }
      };
    }

    this.addStructuredData(structuredData);
  }

  /**
   * Generate person structured data
   * @param personData - Person data
   */
  addPersonStructuredData(personData: PersonData): void {
    const structuredData: StructuredData = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: personData.name,
      description: this.cleanText(personData.description),
      image: personData.imageUrl,
      url: personData.url
    };
    
    if (personData.jobTitle) {
      structuredData.jobTitle = personData.jobTitle;
    }
    
    if (personData.worksFor) {
      structuredData.worksFor = {
        '@type': 'Organization',
        name: personData.worksFor
      };
    }
    
    if (personData.socialProfiles && personData.socialProfiles.length > 0) {
      structuredData.sameAs = personData.socialProfiles.map(profile => profile.url);
    }
    
    this.addStructuredData(structuredData);
  }

  /**
   * Generate organization structured data
   * @param orgData - Organization data
   */
  addOrganizationStructuredData(orgData: OrganizationData): void {
    const structuredData: StructuredData = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: orgData.name,
      description: this.cleanText(orgData.description),
      logo: orgData.logoUrl,
      url: orgData.url
    };
    
    if (orgData.contactPoint) {
      structuredData.contactPoint = {
        '@type': 'ContactPoint',
        ...orgData.contactPoint
      };
    }
    
    if (orgData.address) {
      structuredData.address = {
        '@type': 'PostalAddress',
        ...orgData.address
      };
    }
    
    if (orgData.socialProfiles && orgData.socialProfiles.length > 0) {
      structuredData.sameAs = orgData.socialProfiles.map(profile => profile.url);
    }
    
    this.addStructuredData(structuredData);
  }

  /**
   * Set default SEO tags for the application
   */
  setDefaultSeoTags(): void {
    if(environment.whiteLabelConfig){
      this.setWhiteLableSeo();
    }else{
      this.setSeoMetadata({
        title: 'TopFan - The Best Platform for Creators',
        description: 'TopFan provides creators with their own personal branded website and app, with all their monetization offerings in a single place, so they can finally own and leverage comprehensive data about their fans.',
        keywords: 'creator paywall platform, monetization platform, fan engagement, white labeled, apps, premium subscriptions, merchandise, newsletters, livestreams, personal experiences, paid direct messages, tips',
        imageUrl: this.defaultImageUrl,
        canonicalUrl: this.getCurrentUrl()
      });
    }
  }

  /**
   * Clear all SEO meta tags
   */
  clearSeoTags(): void {
    // Clear title
    this.titleService.setTitle('');
    
    // Clear meta tags
    const metaTags = [
      'description', 'keywords', 'author', 'robots',
      'og:title', 'og:description', 'og:image', 'og:url', 'og:type', 'og:site_name', 'og:locale',
      'article:published_time', 'article:modified_time', 'article:section', 'article:tag',
      'twitter:card', 'twitter:title', 'twitter:description', 'twitter:image', 'twitter:url', 'twitter:site', 'twitter:creator'
    ];
    
    metaTags.forEach(tag => {
      this.meta.removeTag(`name="${tag}"`);
      this.meta.removeTag(`property="${tag}"`);
    });
    
    // Clear structured data
    this.removeAllStructuredData();
  }

  /**
   * Get current URL with caching
   * @returns Current URL with HTTPS protocol
   */
  private getCurrentUrl(): string {
    return this.getCached('current-url', () => {
      if (typeof window !== 'undefined') {
        return window.location.href.replace(/^http:/, 'https:');
      }
      return environment.websiteURL;
    });
  }

  /**
   * Validate SEO metadata
   * @param metadata - SEO metadata to validate
   * @returns Validation result with errors
   */
  validateSeoMetadata(metadata: SeoMetadata): SeoValidationResult {
    const errors: string[] = [];
    
    if (!metadata.title || metadata.title.length === 0) {
      errors.push('Title is required');
    } else if (metadata.title.length > this.MAX_TITLE_LENGTH) {
      errors.push(`Title should be ${this.MAX_TITLE_LENGTH} characters or less`);
    }
    
    if (!metadata.description || metadata.description.length === 0) {
      errors.push('Description is required');
    } else if (metadata.description.length > this.MAX_DESCRIPTION_LENGTH) {
      errors.push(`Description should be ${this.MAX_DESCRIPTION_LENGTH} characters or less`);
    }
    
    if (metadata.keywords && metadata.keywords.length > this.MAX_KEYWORDS_LENGTH) {
      errors.push(`Keywords should be ${this.MAX_KEYWORDS_LENGTH} characters or less`);
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get SEO metadata for current page
   * @returns Current SEO metadata
   */
  getCurrentSeoMetadata(): Partial<SeoMetadata> {
    return {
      title: this.titleService.getTitle(),
      description: this.meta.getTag('name="description"')?.content || '',
      keywords: this.meta.getTag('name="keywords"')?.content || '',
      author: this.meta.getTag('name="author"')?.content || '',
    };
  }

  /**
   * Initialize SEO for the application
   * Sets up basic SEO metadata and structured data
   */
  initializeAppSeo(): void {
    // Set viewport meta tag for mobile optimization with zoom prevention
    this.setViewport('width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no, viewport-fit=cover');
    
    // Set language meta tag
    this.setLanguage('en');
    
    // Set robots meta tag for search engine crawling
    this.setRobots('index, follow');
    
    // Default TopFan configuration
    this.setThemeColor('#007bff');

  if( this.platformService.isBrowser){
   this.meta.updateTag({ name: 'apple-mobile-web-app-status-bar-style', content: 'black' });
  }
    
    // Add organization structured data for TopFan
    this.addOrganizationStructuredData({
      name: 'TopFan',
      description: 'The best platform for creators to monetize their content and build meaningful relationships with their fans.',
      logoUrl: this.defaultImageUrl,
      url: environment.websiteURL,
      contactPoint: {
        contactType: 'customer service',
        email: 'support@topfans.com'
      },
      socialProfiles: [
        { platform: 'Twitter', url: 'https://twitter.com/topfans' },
        { platform: 'Instagram', url: 'https://instagram.com/topfans' },
        { platform: 'Facebook', url: 'https://facebook.com/topfans' }
      ]
    });
  }


  setWhiteLableSeo(): void {
    // Check if this is a white label configuration
    if (environment?.whiteLabelConfig) {
      let whiteLabelData;
      try {
        // Parse white label config if it's a string
        whiteLabelData = typeof environment.whiteLabelConfig === 'string' 
          ? JSON.parse(environment.whiteLabelConfig) 
          : environment.whiteLabelConfig;
      } catch (error) {
        console.error('Error parsing whiteLabelConfig:', error);
        whiteLabelData = null;
      }

      if (whiteLabelData?.data?.whiteLabel && whiteLabelData?.user) {
        // Use white label SEO configuration
        const siteName = whiteLabelData.data.whiteLabel.user_text || 'TopFan';
        const logoUrl = whiteLabelData.user.profile_image_url || whiteLabelData.data.whiteLabel.logo_url || this.defaultImageUrl;
        const bgColor = whiteLabelData.data.whiteLabel.bg_color || Application.DEFAULT_BG_COLOR;
        const user = whiteLabelData.user;
        
        // Set theme color from white label config
        this.setThemeColor(bgColor);
        
        // Set white label SEO metadata
        this.setSeoMetadata({
          title: whiteLabelData?.data?.whiteLabel?.metaTag || `Official Website of ${user.publicly_displayed_name}`,
          description: user?.bio || `${siteName} provides creators with their own personal branded website and app, with all their monetization offerings in a single place.`,
          keywords: `${siteName}, creator platform, fan engagement, content creation, monetization, subscriptions, merchandise, experiences`,
          imageUrl: logoUrl,
          canonicalUrl: environment.websiteURL,
          ogType: 'website',
          siteName: siteName,
          locale: this.defaultLocale,
          author: user?.username || siteName
        });

        // Update app names for iOS/Android install banners
        this.meta.updateTag({ name: 'apple-mobile-web-app-title', content: user.publicly_displayed_name });
        this.meta.updateTag({ name: 'application-name', content: user.publicly_displayed_name });

        
        // Add organization structured data for white label
        this.addOrganizationStructuredData({
          name: siteName,
          description: user?.bio || `${siteName} provides creators with their own personal branded website and app, with all their monetization offerings in a single place.`,
          logoUrl: logoUrl,
          url: environment.websiteURL,
          socialProfiles: user?.social_profiles ? Object.entries(user.social_profiles).map(([platform, url]) => ({
            platform: platform.charAt(0).toUpperCase() + platform.slice(1),
            url: url as string
          })) : []
        });

        // Set favicon and apple touch icon for white label
        // this.setFavicon(logoUrl);
        // this.setAppleTouchIcon(logoUrl);
      }
    }
  }

  /**
   * Set SEO for default TopFan theme
   */
  setDefaultThemeSeo(): void {
    if(environment.whiteLabelConfig){
      this.setWhiteLableSeo();
    }else{
      this.setSeoMetadata({
        title: 'TopFan - The Best Platform for Creators',
        description: 'TopFan provides creators with their own personal branded website and app, with all their monetization offerings in a single place, so they can finally own and leverage comprehensive data about their fans.',
        keywords: 'creator paywall platform, monetization platform, fan engagement, white labeled, apps, premium subscriptions, merchandise, newsletters, livestreams, personal experiences, paid direct messages, tips',
        imageUrl: this.defaultImageUrl,
        canonicalUrl: environment.websiteURL,
        ogType: 'website',
        siteName: this.defaultSiteName,
        locale: this.defaultLocale
      });
    }
  }

  /**
   * Set SEO for white label configurations
   * @param data - White label data
   * @param user - User data
   * @param logoUrl - Logo URL
   */
  setWhiteLabelSeo(data: any, user: any, logoUrl: string): void {
    if( this.platformService.isBrowser){
      this.meta.updateTag({ name: 'apple-mobile-web-app-status-bar-style', content: 'black' });
    }
    const siteName = data?.whiteLabel?.user_text || 'TopFan';
    const siteDescription = user?.bio || `${siteName} provides creators with their own personal branded website and app, with all their monetization offerings in a single place.`;
    const siteKeywords = `${siteName}, creator platform, fan engagement, content creation, monetization, subscriptions, merchandise, experiences`;
    // Set comprehensive SEO metadata for white label
    this.setSeoMetadata({
      title: data?.whiteLabel?.metaTag || `Official Website of ${user.publicly_displayed_name}`,
      description: siteDescription,
      keywords: siteKeywords,
      imageUrl: logoUrl,
      canonicalUrl: environment.websiteURL,
      ogType: 'website',
      siteName: siteName,
      locale: this.defaultLocale,
      author: user?.username || siteName
    });

    

    // Add organization structured data for white label
    this.addOrganizationStructuredData({
      name: siteName,
      description: siteDescription,
      logoUrl: logoUrl,
      url: environment.websiteURL,
      socialProfiles: user?.social_profiles ? Object.entries(user.social_profiles).map(([platform, url]) => ({
        platform: platform.charAt(0).toUpperCase() + platform.slice(1),
        url: url as string
      })) : []
    });

       // Update app names for iOS/Android install banners
       this.meta.updateTag({ name: 'apple-mobile-web-app-title', content: user.publicly_displayed_name });
       this.meta.updateTag({ name: 'application-name', content: user.publicly_displayed_name });

    // Set theme color for browser
    if (data?.whiteLabel?.bg_color) {
      this.setThemeColor(data.whiteLabel.bg_color);
    }
    // this.setFavicon(logoUrl);
    // this.setAppleTouchIcon(logoUrl);
  }

  /**
   * Set SEO for user profile pages
   * @param userData - User profile data
   * @param customTitle - Optional custom title
   */
  setUserProfileSeo(userData: any, customTitle: string = ''): void {
    // this.updateAppleTouchStartupImages();
    if (userData == null) {
      return;
    }
    const userName = `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim();
    const taxonomyData = this.getTaxonomyData(userData);
    const categoryArray = ([] as any[]).concat(...taxonomyData);
    const keywords = this.getKeywordsFromCategories(categoryArray, 'name');
    
    let title: string;
    let description: string;
    let finalKeywords: string;
    
    if (environment.whiteLabelConfig) {
    this.setWhiteLableSeo();
    } else {
      title = customTitle || `${userName} Official Profile and App`;
      finalKeywords = `${keywords}, Official Website, App, iOS App, Android App, Exclusive Content, Merchandise, Fan Club, News, Videos, Photos`;
      description = `${(userData.bio || title).trimEnd()}${keywords ? ' - ' + keywords : ''}`;
    }
    
    const imageUrl = userData.profile_image_url || './assets/images/svg/ic-profile.svg';
    
    // Set comprehensive SEO metadata
    this.setSeoMetadata({
      title: title,
      description: description,
      keywords: `${userName}, ${finalKeywords}`,
      imageUrl: imageUrl,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'profile',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: userName
    });

    // Add person structured data
    this.addPersonStructuredData({
      name: userName,
      description: userData.bio || `${userName} is a content creator on TopFan`,
      imageUrl: imageUrl,
      url: this.getCurrentUrl(),
      jobTitle: 'Content Creator',
      worksFor: this.defaultSiteName,
      socialProfiles: this.getSocialProfiles(userData)
    });
  }

  /**
   * Set SEO for user posts
   * @param postData - Post data
   * @param userName - User name
   */
  setUserPostSeo(postData: any, userName: string): void {
    const desc = `${postData.description}`;
    const title = `${postData.cta_title || postData.description || postData?.item_details?.title} - Official ${postData.type} about ${userName}`;
    
    let description = desc?.length > 300
      ? desc?.slice(0, 200) + '...'
      : desc?.slice(0, 200);

    const keywords = this.getPostKeywords(postData.user_tag);
    description = `${description} - Official news ${postData.type} about ${userName} ${keywords ? ' - ' + keywords : ''}`;
    
    let imageUrl = postData.feed_preview_image_url || postData.image_url || postData.thumbnail_url || './assets/images/svg/ic-profile.svg';
    
    // Handle different post types
    let videoUrl: string | undefined;
    let videoType: string | undefined;
    
    if(postData.type === 'image'){
      imageUrl = postData.media_url?.[0] || imageUrl;
    } else if(postData.type === 'video'){
      imageUrl = postData.feed_preview_image_url || imageUrl;
      // Extract video URL (m3u8 format)
      videoUrl = postData.media_url?.[0] || postData.video_stream_id || undefined;
      // Determine video type based on URL format
      if (videoUrl) {
        if (videoUrl.includes('.m3u8')) {
          videoType = 'application/x-mpegURL'; // HLS format
        } else if (videoUrl.includes('.mp4')) {
          videoType = 'video/mp4';
        } else {
          videoType = 'video/mp4'; // Default fallback
        }
      }
    }
    
    // Set comprehensive SEO metadata
    const ogType = postData.type;
    this.setSeoMetadata({
      title: title,
      description: description,
      keywords: `${userName}, ${postData.type}, ${keywords}`,
      imageUrl: imageUrl,
      canonicalUrl: this.getCurrentUrl(),
      ogType: ogType,
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: userName,
      publishedTime: postData.created_at,
      tags: postData.user_tag || [],
      videoUrl: videoUrl,
      videoType: videoType
    });

    // Add structured data based on post type
    if(postData.type === 'video'){
      // Add VideoObject structured data for video posts
      const videoContentUrl = postData.media_url?.[0] || postData.video_stream_id || '';
      const videoThumbnailUrl = postData.feed_preview_image_url || imageUrl;
      
      // Format upload date (convert timestamp to ISO 8601 if needed)
      let uploadDate = postData.created_at;
      if (typeof uploadDate === 'number') {
        uploadDate = new Date(uploadDate).toISOString();
      } else if (typeof uploadDate === 'string' && !uploadDate.includes('T')) {
        // If it's a timestamp string, convert it
        uploadDate = new Date(parseInt(uploadDate)).toISOString();
      }

      this.addVideoStructuredData({
        name: title,
        description: description,
        thumbnailUrl: videoThumbnailUrl,
        contentUrl: videoContentUrl,
        embedUrl: this.getCurrentUrl(), // Use current page URL as embedUrl (where video player is)
        uploadDate: uploadDate,
        duration: postData.duration, // Pass duration if available
        author: userName,
        publisher: this.defaultSiteName,
        url: this.getCurrentUrl()
      });
    } else {
      // Add article structured data for non-video posts
      this.addArticleStructuredData({
        headline: title,
        description: description,
        imageUrl: imageUrl,
        author: userName,
        publishedDate: postData.created_at,
        url: this.getCurrentUrl()
      });
    }
  }

  /**
   * Set SEO for dynamic SEO pages
   * @param seoData - SEO data object
   */
  setDynamicSeoPage(seoData: any): void {
    if (!seoData) return;
    
    const title = seoData.seo_title;
    const keywords = seoData.keywords?.join(', ') || '';
    const description = seoData.display_description;
    const imageUrl = seoData.hero_img || './assets/images/svg/ic-profile.svg';
    
    // Set comprehensive SEO metadata
    this.setSeoMetadata({
      title: title,
      description: description,
      keywords: keywords,
      imageUrl: imageUrl,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'website',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale
    });
  }

  /**
   * Set SEO for marketing pages (api/admin/marketing-page response).
   * Uses meta_title, meta_description, seoKeywords; OG image from first section hero_image.
   */
  setMarketingPageSeo(seoData: {
    meta_title?: string;
    meta_description?: string;
    seoKeywords?: string;
    title?: string;
    sections?: Array<{ hero_image?: string }>;
  }): void {
    if (!seoData) return;
    const title = seoData.meta_title || seoData.title;
    const description = seoData.meta_description || '';
    const keywords = seoData.seoKeywords || '';
    const imageUrl = seoData.sections?.[0]?.hero_image || this.defaultImageUrl;
    this.setSeoMetadata({
      title: title,
      description: description,
      keywords: keywords,
      imageUrl: imageUrl,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'website',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale
    });
  }

  /**
   * Helper method to get taxonomy data from user profile
   * @param userData - User profile data
   * @returns Taxonomy data array
   */
  private getTaxonomyData(user: any): any[] {
    if (!user?.user_category) {
      user.category.forEach((category: any) => {
        category.options =
          has(category, 'options') && category.options?.length > 0
            ? category.options
            : [[]];
        user.sub_category?.forEach((subCategory: any) => {
          if (category.sk === subCategory.category_id) {
            category.options[0].push(subCategory);
          }
        });
      });
      user.user_category = user.category;
    }
    return user.user_category?.flatMap((category) => {
      return (
        category.options?.map((catBoxes) => {
          const categoryName =
            category.tempCategory_name || category.category_name;
          const catObj = [
            this.createTaxonomyObject(
              category.pk,
              category.sk,
              categoryName,
              category.sk,
              true,
              category?.mainCategory
            )
          ];
          const subCategoryObjects = this.processSubCategories(
            catBoxes as SubCategory[]
          );
          return [...catObj, ...subCategoryObjects];
        }) || []
      );
    });
  }
   processSubCategories(subCategories: SubCategory[]) {
    return subCategories.flatMap((subCat) => {
      if (subCat?.tempCategory_name) {
        const newNames = subCat.tempCategory_name.split(' , ');
        return newNames.map((name, index) => {
          const pk = subCat?.[`${name}PK`] || subCat.pk;
          const sk = subCat?.[`${name}SK`] || subCat.sk;
          const categoryId =
            subCat?.[`${name}category_id`] || subCat.category_id;
          const showProfile_chevron = index < 1 ? true : false;
          const mainCategory = subCat?.mainCategory;
          return this.createTaxonomyObject(
            pk,
            sk,
            name,
            categoryId,
            showProfile_chevron,
            mainCategory
          );
        });
      }
      return this.createTaxonomyObject(
        subCat.pk,
        subCat.sk,
        subCat.category_name,
        subCat.category_id,
        true,
        subCat?.mainCategory
      );
    });
  }
  

  createTaxonomyObject(
    pk: string,
    sk: string,
    name: string,
    categoryId: string,
    showProfile_chevron: boolean,
    mainCategory: string
  ) {
    mainCategory = mainCategory ?? name;
    return { pk, sk, name, categoryId, showProfile_chevron, mainCategory };
  }

  /**
   * Helper method to get keywords from categories
   * @param categories - Category array
   * @param property - Property to extract from categories
   * @returns Keywords string
   */
  private getKeywordsFromCategories(categories: any[], property: string): string {
    return categories.map(cat => cat[property]).filter(Boolean).join(', ');
  }

  /**
   * Helper method to get post keywords
   * @param userTags - User tags array
   * @returns Keywords string
   */
  private getPostKeywords(userTags: any[]): string {
    return userTags ? userTags.join(', ') : '';
  }

  /**
   * Helper method to get social profiles from user data
   * @param userData - User profile data
   * @returns Social profiles array
   */
  private getSocialProfiles(userData: any): Array<{platform: string, url: string}> {
    const profiles: Array<{platform: string, url: string}> = [];
    
    if (userData?.social_profiles) {
      Object.entries(userData.social_profiles).forEach(([platform, url]) => {
        if (url) {
          profiles.push({
            platform: platform.charAt(0).toUpperCase() + platform.slice(1),
            url: url as string
          });
        }
      });
    }
    
    return profiles;
  }

  /**
   * Set SEO for event listing pages
   * @param userProfile - User profile data
   * @param eventDetails - Event calendar details
   * @param creatorHeadingTitle - Creator heading title
   */
  setEventListingSeo(userProfile: any, eventDetails: any, creatorHeadingTitle: string): void {
    
    let title: string;
    let description: string;
    
    if (environment.whiteLabelConfig) {
      title = `${creatorHeadingTitle} - Official Website`;
      description = `${creatorHeadingTitle} Official Events: Locations, dates, times, tickets, pre-sale and VIP`;
    } else {
      title = `${creatorHeadingTitle}`;
      description = `${creatorHeadingTitle}: Locations, dates, times, tickets, pre-sale and VIP`;
    }
    
    const keywords = [
      creatorHeadingTitle,
      'Events, Calendar, Performances, Locations, Tickets, Pre-Sale, VIP, Meet & Greet, presale, pre sale'
    ].join(", ").trim();
    
    const imageUrl = eventDetails?.event_calendar_image_url || './assets/images/svg/ic-profile.svg';
    
    // Set comprehensive SEO metadata
    this.setSeoMetadata({
      title: title,
      description: description,
      keywords: keywords,
      imageUrl: imageUrl,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'website',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: userProfile.user_name
    });

    // Add organization structured data for event calendar
    this.addOrganizationStructuredData({
      name: creatorHeadingTitle,
      description: description,
      logoUrl: imageUrl,
      url: this.getCurrentUrl(),
      socialProfiles: this.getSocialProfiles(userProfile)
    });
  }

  /**
   * Set SEO for specific event details
   * @param userProfile - User profile data
   * @param eventData - Specific event data
   * @param creatorHeadingTitle - Creator heading title
   * @param eventDetails - Additional event details
   */
  setEventDetailsSeo(userProfile: any, eventData: any, creatorHeadingTitle: string, eventDetails: any): void {
    const userName = `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim();
    
    const keywords = [
      userName,
      creatorHeadingTitle,
      eventData.venue_name,
      'events',
      'event calendar',
      `Event Location: ${eventData.venue_name}`,
      `Event Date: ${this.formatDateV2(new Date(eventData.event_date))}`,
      `Event Time: ${this.formatTo12HourTime(eventData.event_time)}`,
      `Location: ${eventData.venue_name}, ${eventData.city}, ${eventData.state}, ${eventData.country}`,
      'Events, Calendar, Performances, Locations, Tickets, Pre-Sale, VIP, Meet & Greet, presale, pre sale'
    ].join(", ").trim();
    
    let title: string;
    let description: string;
    
    if (environment.whiteLabelConfig) {
      title = `${userName} at ${eventData.venue_name} - Official Website`;
      description = `Come see ${creatorHeadingTitle} Official Event at ${eventData.venue_name} in ${eventData.city}, ${eventData.state}, ${eventData.country} on ${this.formatDateV2(new Date(eventData.event_date))} at ${this.formatTo12HourTime(eventData.event_time)}. Find tickets, VIP packages, pre-sale codes, meet & greet opportunities and more. Don't miss out on an unforgettable experience!`;
    } else {
      title = `${userName} at ${eventData.venue_name}`;
      description = `${userName} ${creatorHeadingTitle} at ${eventData.venue_name} in ${eventData.city}, ${eventData.state}, ${eventData.country} on ${this.formatDateV2(new Date(eventData.event_date))} at ${this.formatTo12HourTime(eventData.event_time)}`;
    }
    
    const imageUrl = eventDetails?.event_calendar_image_url || './assets/images/svg/ic-profile.svg';
    
    // Set comprehensive SEO metadata
    this.setSeoMetadata({
      title: title,
      description: description,
      keywords: keywords,
      imageUrl: imageUrl,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'website',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: userProfile.user_name,
      publishedTime: eventData.created_at
    });

    // Build offers with as much detail as available to satisfy Google validations
    const normalizedPrice =
      eventData?.ticket_price ??
      eventData?.price ??
      eventDetails?.ticket_price ??
      eventDetails?.price;
    const normalizedCurrency =
      eventData?.price_currency ??
      eventData?.currency ??
      eventDetails?.price_currency ??
      eventDetails?.currency;
    const normalizedValidFrom =
      eventData?.valid_from ??
      eventData?.created_at ??
      new Date().toISOString();

    const baseEventJson = {
      name: title,
      description: description,
      startDate: eventData.event_date,
      endDate: eventData.event_date,
      location: {
        name: eventData.venue_name,
        address: `${eventData.city}, ${eventData.state}, ${eventData.country}`
      },
      organizer: {
        name: userName,
        url: this.getCurrentUrl()
      },
      image: imageUrl,
      url: this.getCurrentUrl(),
      eventStatus: 'https://schema.org/EventScheduled',
      performer: [
        {
          name: userName,
          url: this.getCurrentUrl()
        }
      ]
    };

    // Add event structured data
    if (normalizedPrice !== undefined && normalizedPrice !== null) {
      this.addEventStructuredData({
        ...baseEventJson,
        offers: {
          '@type': 'Offer',
          url: (eventData.ticket_url || eventDetails?.ticket_url || this.getCurrentUrl()),
          price: normalizedPrice,
          priceCurrency: (normalizedCurrency || 'USD'),
          validFrom: normalizedValidFrom,
          availability: 'https://schema.org/InStock'
        }
      });
    } else {
      // If price is unknown, avoid partial Offer (which triggers warnings); mark as free if no ticket URL
      const isFree = !(eventData.ticket_url || eventDetails?.ticket_url);
      const eventJson: any = { ...baseEventJson };
      if (isFree) {
        eventJson.isAccessibleForFree = true;
      } else {
        // We at least include an offer URL to indicate tickets exist; Google may still warn about price,
        // but this provides the URL. Prefer AggregateOffer only when we have prices.
        eventJson.offers = {
          '@type': 'Offer',
          url: (eventData.ticket_url || eventDetails?.ticket_url || this.getCurrentUrl()),
          availability: 'https://schema.org/InStock'
        };
      }
      this.addEventStructuredData(eventJson);
    }
  }

  /**
   * Add event structured data
   * @param eventData - Event data for structured data
   */
  private addEventStructuredData(eventData: {
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    location: { name: string; address: string };
    organizer: { name: string; url: string };
    image: string;
    url: string;
    eventStatus?: string; // https://schema.org/EventStatusType
    performer?: Array<{ name: string; url?: string }>;
    offers?: {
      '@type': 'Offer' | 'AggregateOffer';
      url: string;
      price?: string | number;
      priceCurrency?: string;
      availability?: string;
      validFrom?: string;
    };
  }): void {
    const structuredData: StructuredData = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: eventData.name,
      description: eventData.description,
      startDate: eventData.startDate,
      endDate: eventData.endDate,
      location: {
        '@type': 'Place',
        name: eventData.location.name,
        address: eventData.location.address
      },
      organizer: {
        '@type': 'Person',
        name: eventData.organizer.name,
        url: eventData.organizer.url
      },
      image: eventData.image,
      url: eventData.url
    };

    // Optional enhancements for richer appearance
    if (eventData.eventStatus) {
      structuredData['eventStatus'] = eventData.eventStatus;
    } else {
      structuredData['eventStatus'] = 'https://schema.org/EventScheduled';
    }

    if (eventData.performer && eventData.performer.length) {
      structuredData['performer'] = eventData.performer.map(p => ({
        '@type': 'Person',
        name: p.name,
        ...(p.url ? { url: p.url } : {})
      }));
    }

    if (eventData.offers && eventData.offers.url) {
      structuredData['offers'] = {
        '@type': eventData.offers['@type'] || 'Offer',
        url: eventData.offers.url,
        ...(eventData.offers.price !== undefined ? { price: eventData.offers.price } : {}),
        ...(eventData.offers.priceCurrency ? { priceCurrency: eventData.offers.priceCurrency } : {}),
        availability: eventData.offers.availability || 'https://schema.org/InStock',
        ...(eventData.offers.validFrom ? { validFrom: eventData.offers.validFrom } : {})
      };
    }
    
    this.addStructuredData(structuredData);
  }

  /**
   * Add an ItemList of Events structured data for event listing pages
   * @param events - Array of event items from API
   * @param userProfile - User profile for organizer/performer context
   * @param calendarImageUrl - Image to use for events without images
   */
  addEventListStructuredData(events: any[], userProfile: any, calendarImageUrl?: string): void {
    if (!events || !Array.isArray(events) || events.length === 0) {
      return;
    }

    const userName = `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim();
    const fallbackImage = calendarImageUrl || this.defaultImageUrl;

    const itemList = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: events
        .filter((e) => !!e?.sk)
        .map((e, index) => {
          const eventUrl = e?.ticket_url || this.getCurrentUrl();
          const city = e?.city || '';
          const state = e?.state || '';
          const country = e?.country || '';
          const locationName = e?.venue_name || 'Venue';
          const eventName = `${userName} at ${locationName}`.trim();

          const eventJson: any = {
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Event',
              name: eventName,
              startDate: e?.event_date,
              endDate: e?.event_date,
              url: eventUrl,
              image: fallbackImage,
              location: {
                '@type': 'Place',
                name: locationName,
                address: [city, state || country].filter(Boolean).join(', ')
              },
              organizer: {
                '@type': 'Person',
                name: userName,
                url: this.getCurrentUrl()
              },
              eventStatus: 'https://schema.org/EventScheduled',
              performer: [{
                '@type': 'Person',
                name: userName
              }]
            }
          };

          return eventJson;
        })
    };

    this.addStructuredData(itemList as any);
  }

  /**
   * Helper method to format date (placeholder - would need actual implementation)
   * @param date - Date to format
   * @returns Formatted date string
   */
  private formatDateV2(date: Date): string {
    // This would need to be implemented based on your date formatting utility
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    const year = date.getFullYear();
    const month = monthNames[date.getMonth()];
    const day = date.getDate().toString();
    return `${month} ${day}, ${year}`;
  }

  /**
   * Helper method to format time (placeholder - would need actual implementation)
   * @param time - Time to format
   * @returns Formatted time string
   */
  private formatTo12HourTime(time: string): string {
    if (!time) {
      return '';
    }
    if (time.split(' ')?.length === 2) {
      return time;
    }
    const [hoursStr, minutesStr] = time.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const minutesFormatted = minutes < 10 ? '0' + minutes : minutes;
      return `${hours}:${minutesFormatted} ${ampm}`;
  }

  /**
   * Set SEO for portfolio listing pages
   * @param userProfile - User profile data
   * @param portfolioListing - Portfolio items array
   * @param portfolioHeading - Portfolio heading
   * @param coverImage - Cover image URL
   * @param fullName - User's full name
   */
  setPortfolioListingSeo(userProfile: any, portfolioListing: any[], portfolioHeading: string, coverImage: string, fullName: string): void {
    let portfolioTitle: string[] = [];
    if (portfolioListing?.length > 0) {
      portfolioTitle = portfolioListing.map((portfolio) => portfolio.title);
    }
    
    const keywords = [
      fullName,
      userProfile?.user_name,
      `${portfolioHeading || 'Portfolio'}`,
      'portfolio',
      'streaming',
      'purchase',
      'buy',
      'watch',
      'listen',
      ...portfolioTitle
    ].join(', ').trim();
    
    let description: string;
    if (environment.whiteLabelConfig) {
      description = `${fullName} portfolio including ${portfolioTitle.join(', ')}`;
    } else {
      description = `${fullName} portfolio of work including ${portfolioTitle.join(', ')}`;
    }
    
    const seoTitle = this.getPortfolioSeoTitle(fullName, portfolioHeading, false);
    
    // Set comprehensive SEO metadata
    this.setSeoMetadata({
      title: seoTitle,
      description: description,
      keywords: keywords,
      imageUrl: coverImage,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'website',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: fullName
    });

    // Add organization structured data for portfolio
    this.addOrganizationStructuredData({
      name: `${fullName} Portfolio`,
      description: description,
      logoUrl: coverImage,
      url: this.getCurrentUrl(),
      socialProfiles: this.getSocialProfiles(userProfile)
    });
  }

  /**
   * Set SEO for specific portfolio item pages
   * @param userProfile - User profile data
   * @param portfolioItem - Specific portfolio item
   * @param fullName - User's full name
   * @param userName - User name
   */
  setPortfolioItemSeo(portfolioItem: any, fullName: string, userName: string): void {
    let dspText: string[] = [];
    if (portfolioItem?.dsp?.length > 0) {
      dspText = portfolioItem.dsp.map((dsp) => `${dsp.name}: ${dsp.text}`);
    }
    
    const keywords = [
      portfolioItem.title,
      fullName,
      userName,
      portfolioItem.title,
      'portfolio',
      'streaming',
      'purchase',
      'buy',
      'watch',
      'listen',
      'where to buy, how to buy, stream, watch, view',
      ...dspText
    ].join(', ').trim();
    
    let description: string;
    if (environment.whiteLabelConfig) {
      description = `${fullName}: ${portfolioItem.description}`;
    } else {
      description = `${fullName} ${portfolioItem.title} ${portfolioItem.description} ${dspText.join(', ')}`;
    }
    
    const seoTitle = this.getPortfolioSeoTitle(fullName, portfolioItem.title, true);
    
    // Set comprehensive SEO metadata
    this.setSeoMetadata({
      title: seoTitle,
      description: description,
      keywords: keywords,
      imageUrl: portfolioItem.img,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'article',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: fullName,
      publishedTime: portfolioItem.created_at,
      tags: dspText
    });

    // Add article structured data for portfolio item
    this.addArticleStructuredData({
      headline: seoTitle,
      description: description,
      imageUrl: portfolioItem.img,
      author: fullName,
      publishedDate: portfolioItem.created_at,
      url: this.getCurrentUrl()
    });
  }

  /**
   * Helper method to generate portfolio SEO title
   * @param fullName - User's full name
   * @param title - Portfolio title or heading
   * @param isItemPage - Whether it's a specific item page
   * @returns SEO title
   */
  private getPortfolioSeoTitle(fullName: string, title: string, isItemPage: boolean): string {
    if (isItemPage) {
      if (environment.whiteLabelConfig) {
        return `${fullName}: ${title}`;
      } else {
        return `${fullName} ${title}`;
      }
    } else {
      if (environment.whiteLabelConfig) {
        return `${title || 'Portfolio'} of ${fullName}`;
      } else {
        return `${fullName} ${title || 'Portfolio'}`;
      }
    }
  }

  setPortfolioTypeSeo(portfolioType: any, albums: any[], individualItems: any[], userProfile: any, fullName: string): void {
    const portfolioTitle = portfolioType?.title || 'Portfolio';
    const seoTitle = `${portfolioTitle} of ${fullName}`;
    const type = portfolioType?.portfolio_type || '';
    const terms = getPortfolioTypeTerms(type);
    
    const albumTitles = albums?.map((album) => album.title || album.album_title || '').filter(Boolean) || [];
    const itemTitles = individualItems?.map((item) => item.title || '').filter(Boolean) || [];
    
    const keywords = [
      portfolioTitle,
      fullName,
      userProfile?.user_name || '',
      type || 'portfolio',
      'portfolio',
      'streaming',
      'purchase',
      'buy',
      terms.action,
      ...albumTitles,
      ...itemTitles
    ].filter(Boolean).join(', ').trim();
    
    const collectionCount = albums?.length || Application.ZERO;
    const itemCount = individualItems?.length || Application.ZERO;
    const portfolioDescription = portfolioType?.description || '';
    
    let description = `${fullName}'s ${portfolioTitle}`;
    if (collectionCount > Application.ZERO && itemCount > Application.ZERO) {
      description += ` featuring ${collectionCount} ${collectionCount === Application.ONE ? terms.collection : terms.collections} and ${itemCount} ${itemCount === Application.ONE ? terms.item : terms.items}`;
    } else if (collectionCount > Application.ZERO) {
      description += ` featuring ${collectionCount} ${collectionCount === Application.ONE ? terms.collection : terms.collections}`;
    } else if (itemCount > Application.ZERO) {
      description += ` featuring ${itemCount} ${itemCount === Application.ONE ? terms.item : terms.items}`;
    }
    if (portfolioDescription) {
      description += `. ${portfolioDescription}`;
    }
    
    this.setSeoMetadata({
      title: seoTitle,
      description: description,
      keywords: keywords,
      imageUrl: portfolioType?.thumbnail || portfolioType?.cover_image_url || userProfile?.banner_image_url || this.defaultImageUrl,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'website',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: fullName
    });

    this.addOrganizationStructuredData({
      name: `${fullName} ${portfolioTitle}`,
      description: description,
      logoUrl: portfolioType?.thumbnail || portfolioType?.cover_image_url || userProfile?.banner_image_url || this.defaultImageUrl,
      url: this.getCurrentUrl(),
      socialProfiles: this.getSocialProfiles(userProfile)
    });
  }

  /**
   * Set SEO for portfolio collection pages (album, playlist, series, etc.)
   * Title format: "Full Name : Collection Title"
   */
  setPortfolioAlbumSeo(albumData: any, userProfile: any, fullName: string, portfolioType?: string): void {
    const albumTitle = albumData?.album_title || albumData?.title || '';
    const seoTitle = `${fullName} : ${albumTitle}`;
    const terms = getPortfolioTypeTerms(portfolioType || '');
    
    const itemTitles = albumData?.items?.map((item: any) => item.title || '').filter(Boolean) || [];
    const itemCount = albumData?.items?.length || albumData?.individual_count || Application.ZERO;
    
    const keywords = [
      albumTitle,
      fullName,
      userProfile?.user_name || '',
      terms.collection,
      portfolioType || 'content',
      'streaming',
      'purchase',
      'buy',
      terms.action,
      ...itemTitles
    ].filter(Boolean).join(', ').trim();
    
    const albumDescription = albumData?.album_desc || albumData?.description || '';
    
    let description = `${albumTitle} by ${fullName}`;
    if (itemCount > Application.ZERO) {
      description += ` featuring ${itemCount} ${itemCount === 1 ? terms.item : terms.items}`;
    }
    if (albumDescription) {
      description += `. ${albumDescription}`;
    }
    
    this.setSeoMetadata({
      title: seoTitle,
      description: description,
      keywords: keywords,
      imageUrl: albumData?.cover_image_url || userProfile?.banner_image_url || this.defaultImageUrl,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'article',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: fullName,
      publishedTime: albumData?.created_at || albumData?.release_date
    });

    this.addArticleStructuredData({
      headline: seoTitle,
      description: description,
      imageUrl: albumData?.cover_image_url || userProfile?.banner_image_url || this.defaultImageUrl,
      author: fullName,
      publishedDate: albumData?.created_at || albumData?.release_date,
      url: this.getCurrentUrl()
    });
  }

  /**
   * Set SEO for individual portfolio item detail pages (song, video, episode, etc.)
   * Title format: "Full Name : Item Title"
   */
  setPortfolioItemDetailSeo(itemData: any, userProfile: any, fullName: string, portfolioType?: string): void {
    const itemTitle = itemData?.title || itemData?.content_title || '';
    const seoTitle = `${fullName} : ${itemTitle}`;
    const terms = getPortfolioTypeTerms(portfolioType || '');
    
    const itemDescription = itemData?.description || itemData?.content_desc || '';
    
    const keywords = [
      itemTitle,
      fullName,
      userProfile?.user_name || '',
      terms.item,
      portfolioType || 'content',
      'streaming',
      'purchase',
      'buy',
      terms.action,
      'download'
    ].filter(Boolean).join(', ').trim();
    
    let description = `${itemTitle} by ${fullName}`;
    if (itemDescription) {
      description += `. ${itemDescription}`;
    }
    
    this.setSeoMetadata({
      title: seoTitle,
      description: description,
      keywords: keywords,
      imageUrl: itemData?.cover_image_url || itemData?.thumbnail || userProfile?.banner_image_url || this.defaultImageUrl,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'article',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: fullName,
      publishedTime: itemData?.created_at || itemData?.release_date
    });

    this.addArticleStructuredData({
      headline: seoTitle,
      description: description,
      imageUrl: itemData?.cover_image_url || itemData?.thumbnail || userProfile?.banner_image_url || this.defaultImageUrl,
      author: fullName,
      publishedDate: itemData?.created_at || itemData?.release_date,
      url: this.getCurrentUrl()
    });
  }

  /**
   * Set SEO for merchandise store pages
   * @param fullName - User's full name
   * @param currFilter - Current filter type
   * @param profileImageUrl - User's profile image URL
   */
  setMerchandiseStoreSeo(fullName: string, currFilter: string, profileImageUrl: string): void {
    let title: string;
    let description: string;
    let keywords: string;
    
    if (!currFilter || currFilter === 'ALL') {
      title = `${fullName}'s Official Store`;
      description = `${fullName}'s Official Store featuring Exclusive Merchandise, Courses, One on One Experiences, NFTs, and Downloadables.`;
      keywords = `${fullName}, Store, Merchandise, Merch, Experiences, Downloadables, NFTs, Courses, T-Shirt, Hat, Hoodie, Collectables, e-book, training`;
    } else {
      const filterData = this.getMerchandiseFilterData(currFilter, fullName);
      title = `${fullName} Official ${filterData.titleValue}`;
      description = filterData.description;
      keywords = filterData.keywords;
    }
    
    // Handle white label vs regular environment
    const finalTitle = environment.whiteLabelConfig 
      ? title 
      : `Official ${currFilter === 'ALL' ? 'Store' : this.getFilterDisplayName(currFilter)} of ${fullName}`;
    
    // Set comprehensive SEO metadata
    this.setSeoMetadata({
      title: finalTitle,
      description: description,
      keywords: keywords,
      imageUrl: profileImageUrl || this.defaultImageUrl,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'website',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: fullName
    });

    // Add organization structured data for store
    this.addOrganizationStructuredData({
      name: `${fullName}'s Store`,
      description: description,
      logoUrl: profileImageUrl || this.defaultImageUrl,
      url: this.getCurrentUrl()
    });
  }

  /**
   * Get merchandise filter data for SEO
   * @param filterType - Filter type
   * @param fullName - User's full name
   * @returns Filter data object
   */
  private getMerchandiseFilterData(filterType: string, fullName: string): {
    titleValue: string;
    description: string;
    keywords: string;
  } {
    switch (filterType) {
      case 'COURSE':
        return {
          titleValue: 'Courses',
          description: `Explore ${fullName} exclusive courses and educational content.`,
          keywords: `${fullName}, courses, e-course, course, ecourse, master class, masterclass, learning, Education, video, official`
        };
      case 'EXPERIENCE':
        return {
          titleValue: 'Experiences',
          description: `Book one-on-one experiences and exclusive sessions with ${fullName} including personalized videos, video calls, and social follows`,
          keywords: `${fullName}, experiences, experience, One-on-One, videos, video calls, and social follows, Cameo, personal video, recorded video, follow on social media, 1 on 1, sessions, official`
        };
      case 'DOWNLOADABLES':
        return {
          titleValue: 'Downloadables',
          description: `Download exclusive digital content and resources from ${fullName} including e-books and files`,
          keywords: `${fullName}, Downloadables, Digital Content, Resources, e-book, training, downloadable, ebook, recipes, instructions, learning, education, files, official`
        };
      case 'NFT':
        return {
          titleValue: 'NFTs',
          description: `Discover and collect unique NFTs from ${fullName}`,
          keywords: `${fullName}, NFT, NFTs, Collectables, Meta Mask, OpenSea, etheruem, ETH, Blockchain, Web 3.0, exclusive, official`
        };
      case 'MERCHANDISE':
        return {
          titleValue: 'Merchandise',
          description: `Shop ${fullName} official merchandise including T-shirts, logo wear, hoodies, and collectibles`,
          keywords: `${fullName}, Merchandise, T-shirts, Hoodies, Collectibles, logo wear, clothing line, official merchandise, official, tshirts`
        };
      default:
        return {
          titleValue: this.toTitleCase(filterType),
          description: `${fullName} Official Store featuring Exclusive Merchandise, Courses, One on One Experiences, NFTs, and Downloadables.`,
          keywords: `${fullName}, Store, Merchandise, Merch, Experiences, Downloadables, NFTs, Courses, T-Shirt, Hat, Hoodie, Collectables, e-book, training`
        };
    }
  }

  /**
   * Helper method to convert string to title case
   * @param str - String to convert
   * @returns Title case string
   */
  private toTitleCase(str: string): string {
    return str.replace(/\w\S*/g, (txt) => 
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  }

  /**
   * Helper method to get display name for filter types
   * @param filterType - Filter type
   * @returns Display name
   */
  private getFilterDisplayName(filterType: string): string {
    const filterMap: { [key: string]: string } = {
      'COURSE': 'Courses',
      'EXPERIENCE': 'Experiences',
      'DOWNLOADABLES': 'Downloadables',
      'NFT': 'NFTs',
      'MERCHANDISE': 'Merchandise'
    };
    return filterMap[filterType] || filterType;
  }

  /**
   * Set SEO for newsletter listing pages
   * @param userProfile - User profile data
   */
  setNewsletterListingSeo(userProfile: any): void {
    if (!userProfile?.user_name) {
      return;
    }
    
    const creatorName = userProfile?.first_name && userProfile?.last_name
      ? `${userProfile.first_name} ${userProfile.last_name}`
      : userProfile?.user_name || 'Creator';
    
    // Generate SEO keywords
    const keywords = [
      creatorName,
      userProfile?.user_name,
      'newsletter',
      'news letter',
      'exclusive content',
      'creator',
      'TopFan',
      'subscription',
      'premium content',
      'content, news'
    ].filter(keyword => keyword && keyword.trim()).join(', ');
    
    let title: string;
    let description: string;
    
    if (environment.whiteLabelConfig) {
      title = `${creatorName} Official Newsletters and Information`;
      description = `Newsletters by ${creatorName}: Exclusive newsletter content directly from ${creatorName} including news, photos, videos, and more.`;
    } else {
      title = `${creatorName} Official Newsletters`;
      description = `Read ${creatorName} Official Newsletters by ${creatorName}. Exclusive newsletter content directly from ${creatorName} including news, photos, videos, and more.`;
    }
    
    // Set comprehensive SEO metadata
    this.setSeoMetadata({
      title: title,
      description: description,
      keywords: keywords,
      imageUrl: userProfile.banner_image_url || this.defaultImageUrl,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'website',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: userProfile?.user_name
    });

    // Add organization structured data for newsletter
    this.addOrganizationStructuredData({
      name: `${creatorName} Newsletters`,
      description: description,
      logoUrl: userProfile.banner_image_url || this.defaultImageUrl,
      url: this.getCurrentUrl(),
      socialProfiles: this.getSocialProfiles(userProfile)
    });
  }

  /**
   * Set SEO for specific newsletter view pages
   * @param newsletterData - Newsletter data object
   * @param userProfile - User profile data
   */
  setNewsletterViewSeo(newsletterData: any, userProfile: any): void {
    const newsletterTitle = newsletterData?.title || 'Newsletter';
    const creatorName = userProfile?.first_name && userProfile?.last_name
      ? `${userProfile.first_name} ${userProfile.last_name}`
      : userProfile?.user_name || 'Creator';
    
    let seoTitle: string;
    let seoDescription: string;
    
    if (environment.whiteLabelConfig) {
      seoTitle = `Newsletter by ${creatorName}: ${newsletterTitle}`;
      seoDescription = `Newsletter: ${newsletterTitle} by ${creatorName}. Exclusive newsletter content from ${creatorName}.`;
    } else {
      seoTitle = `${newsletterTitle} by ${creatorName}`;
      seoDescription = `Read ${newsletterTitle} by ${creatorName}. Exclusive newsletter content from ${creatorName}.`;
      
      if (newsletterData?.content) {
        // Strip HTML tags and truncate
        const textContent = newsletterData.content.replace(/<[^>]*>/g, '').trim();
        if (textContent.length > 0) {
          seoDescription = textContent.substring(0, 160) + (textContent.length > 160 ? '...' : '');
        }
      }
    }
    
    const seoImage = newsletterData?.preview_image_url ||
                    userProfile?.profile_pic ||
                    this.defaultImageUrl;
    
    // Generate SEO keywords
    const keywords = [
      newsletterTitle,
      creatorName,
      userProfile?.user_name,
      'newsletter',
      'exclusive content',
      'news letter',
      'creator',
      'TopFan',
      'subscription',
      'premium content',
      'news'
    ].filter(keyword => keyword && keyword.trim()).join(', ');
    
    // Set comprehensive SEO metadata
    this.setSeoMetadata({
      title: seoTitle,
      description: seoDescription,
      keywords: keywords,
      imageUrl: seoImage,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'article',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: creatorName,
      publishedTime: newsletterData?.created_at,
      tags: ['newsletter', 'exclusive content', 'creator content']
    });

    // Add article structured data for newsletter
    this.addArticleStructuredData({
      headline: seoTitle,
      description: seoDescription,
      imageUrl: seoImage,
      author: creatorName,
      publishedDate: newsletterData?.created_at,
      url: this.getCurrentUrl()
    });
  }

  /**
   * Set SEO for merchandise details pages
   * @param productDetails - Product/merchandise details
   * @param userProfile - User profile data
   * @param contentType - Type of content (MERCHANDISE, COURSE, EXPERIENCE, etc.)
   */
  setMerchandiseDetailsSeo(productDetails: any, userProfile: any, contentType: string): void {
    const { title, description, name } = productDetails;
    const fullName = `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim();
    
    // Generate SEO keywords based on content type
    const keywords = this.getMerchandiseDetailsKeywords(title || name, contentType, fullName);
    
    // Generate SEO title
    const seoTitle = `${fullName} ${title || name}`;
    
    // Generate description based on content type
    let seoDescription: string;
    if (contentType === 'Merchandise') {
      seoDescription = `${title}: ${this.extractTextFromHTML(description)}, merchandise from ${fullName}`;
    } else {
      seoDescription = `${this.extractTextFromHTML(description)} ${productDetails.monatize_type} ${fullName}`;
    }
    
    // Construct proper canonical URL for merchandise pages
    const canonicalUrl = this.constructMerchandiseCanonicalUrl(productDetails, userProfile, contentType);
    
    // Set comprehensive SEO metadata
    this.setSeoMetadata({
      title: seoTitle,
      description: seoDescription,
      keywords: keywords,
      imageUrl:
        productDetails.imagePath ||
        productDetails?.images?.[0]?.src ||
        this.defaultImageUrl,
      canonicalUrl: canonicalUrl,
      ogType: 'website',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: fullName,
      publishedTime: productDetails.created_at,
      tags: [contentType.toLowerCase(), 'exclusive', 'creator content']
    });

    const productPrice = this.extractProductPrice(productDetails);
    const productAvailability = this.extractProductAvailability(productDetails);

    this.addProductStructuredData({
      name: title || name,
      description: seoDescription,
      imageUrl: productDetails.imagePath || productDetails?.images?.[0]?.src || this.defaultImageUrl,
      brand: fullName,
      url: this.getCurrentUrl(),
      price: productPrice,
      currency: 'USD',
      availability: productAvailability
    });
  }

  /**
   * Set SEO for NFT product details pages
   * @param nftData - NFT product data
   * @param userProfile - User profile data
   */
  setNftDetailsSeo(nftData: any, userProfile: any): void {
    const { title, description, name } = nftData;
    const fullName = `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim();
    
    const keywords = `${title}, nft, ${fullName}, ${userProfile?.user_name || ''}, buy, purchase, exclusive, NFT, NFTs, creator, drop, file`.replace(/,\s*,/g, ',').replace(/^,|,$/g, '');
    
    const seoTitle = `${fullName} ${title || name}`;
    const seoDescription = `${description} ${fullName}`;
    
    // Set comprehensive SEO metadata
    this.setSeoMetadata({
      title: seoTitle,
      description: seoDescription,
      keywords: keywords,
      imageUrl: nftData.imagePath || this.defaultImageUrl,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'website',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: fullName,
      publishedTime: nftData.created_at,
      tags: ['nft', 'exclusive', 'creator content', 'blockchain']
    });

    this.addProductStructuredData({
      name: title || name,
      description: seoDescription,
      imageUrl: nftData.imagePath || this.defaultImageUrl,
      brand: fullName,
      url: this.getCurrentUrl(),
      price: this.extractProductPrice(nftData),
      currency: 'USD',
      availability: 'InStock'
    });
  }

  /**
   * Generate keywords for merchandise details based on content type
   * @param title - Product title
   * @param contentType - Content type
   * @param fullName - Creator's full name
   * @param userName - Creator's username
   * @returns Generated keywords string
   */
  private getMerchandiseDetailsKeywords(title: string, contentType: string, fullName: string): string {
    const baseKeywords = [
      title,
      contentType,
      fullName,
      'buy',
      'purchase',
      'exclusive',
      'e-book',
      'ebook',
      'instructions',
      'training',
      'learning',
      'education',
      'file',
      'e-course',
      'master class',
      'masterclass'
    ].filter(keyword => keyword && keyword.trim());
    
    return baseKeywords.join(', ').replace(/,\s*,/g, ',').replace(/^,|,$/g, '');
  }

  /**
   * Construct proper canonical URL for merchandise pages
   * @param productDetails - Product/merchandise details
   * @param userProfile - User profile data
   * @param contentType - Type of content (MERCHANDISE, COURSE, EXPERIENCE, etc.)
   * @returns Properly constructed canonical URL
   */
  private constructMerchandiseCanonicalUrl(productDetails: any, userProfile: any, contentType: string): string {
    const baseUrl = environment.websiteURL.replace(/\/$/, '');
    const userName = userProfile?.user_name;
    
    if (!userName) {
      return baseUrl;
    }
    
    // Construct URL based on content type
    if (contentType === 'Merchandise' || contentType === 'NFT') {
      // For merchandise and NFT: /dashboard/merchandise-detail/{username}/{id}/store/{type}
      const productId = productDetails.id;
      const type = contentType.toLowerCase();
      return `${baseUrl}/dashboard/merchandise-detail/${userName}/${productId}/store/${type}`;
    } else {
      // For DOWNLOADABLES: Simplified URL structure - /{username}&{gsi4pk}/downloadable/{id}/{type}
      const encodedUserParam = `${userName}&${userProfile.gsi4pk}`;
      if (productDetails.monatize_type === 'DOWNLOADABLES') {
        const downloadableId = productDetails.sk.split('#')[1]; // Extract ID from "DOWNLOADABLES#1749678524636#USER#..."
        return `${baseUrl}/dashboard/merchandise-details/${encodedUserParam}/downloadable/${downloadableId}/DOWNLOADABLES`;
      } else {
        // For EXPERIENCE, COURSE: Use single encoding to match route generation
        const encodedPk = encodeURIComponent(productDetails.pk);
        const encodedSk = encodeURIComponent(productDetails.sk);
        return `${baseUrl}/dashboard/merchandise-details/${encodedUserParam}/${encodedPk}/${encodedSk}/store`;
      }
    }
  }

  /**
   * Extract text from HTML content
   * @param html - HTML content
   * @returns Plain text
   */
  private extractTextFromHTML(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  }

  private extractProductPrice(productDetails: any): number {
    const directPrice = parseFloat(productDetails.price);
    if (!isNaN(directPrice) && directPrice > 0) {
      return directPrice;
    }

    const variantPrice = parseFloat(productDetails?.variants?.[0]?.price);
    if (!isNaN(variantPrice) && variantPrice > 0) {
      return variantPrice;
    }

    const individualPrice = parseFloat(productDetails.individual_price);
    if (!isNaN(individualPrice) && individualPrice > 0) {
      return individualPrice;
    }

    return 0;
  }

  private extractProductAvailability(productDetails: any): string {
    if (productDetails.inventory_quantity > 0) {
      return 'InStock';
    }

    const hasVariantInStock = productDetails?.variants?.some(
      (v: any) => v.inventory_quantity > 0
    );
    if (hasVariantInStock) {
      return 'InStock';
    }

    return 'OutOfStock';
  }

  private addProductStructuredData(productData: {
    name: string;
    description: string;
    imageUrl: string;
    brand: string;
    url: string;
    price?: number;
    currency?: string;
    availability?: string;
  }): void {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: productData.name,
      description: productData.description,
      image: productData.imageUrl,
      brand: {
        '@type': 'Brand',
        name: productData.brand
      },
      url: productData.url
    };

    const numericPrice = parseFloat(String(productData.price)) || 0;

    structuredData['offers'] = {
      '@type': 'Offer',
      price: numericPrice.toFixed(2),
      priceCurrency: productData.currency || 'USD',
      availability: `https://schema.org/${productData.availability || 'InStock'}`,
      url: productData.url
    };

    this.addStructuredData(structuredData);
  }

  /**
   * Set SEO for authentication pages (login, signup, forgot-password)
   * @param seoData - SEO data from constants
   */
  setAuthPageSeo(seoData: any): void {
    // Check if this is a white label configuration
    const whiteLabelData = extractWhiteLabelConfig();

    if (whiteLabelData?.data?.whiteLabel && whiteLabelData?.user) {
      // Use white label SEO configuration for auth pages
      const siteName = whiteLabelData.data.whiteLabel.user_text || 'TopFan';
      const logoUrl = whiteLabelData.user.profile_image_url || whiteLabelData.data.whiteLabel.logo_url || this.defaultImageUrl;
      
      // Set white label SEO metadata for auth pages
      this.setSeoMetadata({
        title: `${seoData.title} - ${siteName}`,
        description: seoData.desc.replace('TopFan', siteName),
        keywords: `${seoData.keywords}, ${siteName}`,
        imageUrl: logoUrl,
        canonicalUrl: this.getCurrentUrl(),
        ogType: 'website',
        siteName: siteName,
        locale: this.defaultLocale,
        author: siteName
      });

      // Add organization structured data for white label auth pages
      this.addOrganizationStructuredData({
        name: siteName,
        description: seoData.desc.replace('TopFan', siteName),
        logoUrl: logoUrl,
        url: this.getCurrentUrl()
      });

      // Set theme color and favicon for white label
      if (whiteLabelData.data.whiteLabel.bg_color) {
        this.setThemeColor(whiteLabelData.data.whiteLabel.bg_color);
      }
      // this.setFavicon(logoUrl);
      
      return; // Exit early for white label configuration
    }

    // Default SEO configuration for non-white label sites
    this.setSeoMetadata({
      title: seoData.title,
      description: seoData.desc,
      keywords: seoData.keywords,
      imageUrl: this.defaultImageUrl,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'website',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: this.defaultSiteName
    });

    // Add organization structured data for auth pages
    this.addOrganizationStructuredData({
      name: this.defaultSiteName,
      description: seoData.desc,
      logoUrl: this.defaultImageUrl,
      url: this.getCurrentUrl()
    });
  }

  /**
   * Set SEO for public pages (about, pricing, contact, etc.)
   * @param seoData - SEO data from constants
   */
  setPublicPageSeo(seoData: any): void {
    // Set comprehensive SEO metadata
    this.setSeoMetadata({
      title: seoData.title,
      description: seoData.desc,
      keywords: seoData.keywords,
      imageUrl: this.defaultImageUrl,
      canonicalUrl: this.getCurrentUrl(),
      ogType: 'website',
      siteName: this.defaultSiteName,
      locale: this.defaultLocale,
      author: this.defaultSiteName
    });

    // Add organization structured data for public pages
    this.addOrganizationStructuredData({
      name: this.defaultSiteName,
      description: seoData.desc,
      logoUrl: this.defaultImageUrl,
      url: this.getCurrentUrl()
    });
  }

  /**
   * Set SEO for forum/chat room pages
   * @param creatorName - Creator's full name
   * @param userProfile - User profile data (optional)
   */
  setForumSeo(creatorName: string, userProfile?: any): void {
    const fullName = creatorName.trim();
    const currentUrl = this.getCurrentUrl();
    
    // Check if this is a white label configuration
    const whiteLabelData = extractWhiteLabelConfig();
    
    if (whiteLabelData?.data?.whiteLabel && whiteLabelData?.user) {
      // Use white label SEO configuration for forum
      const siteName = whiteLabelData.data.whiteLabel.user_text || 'TopFan';
      const logoUrl = whiteLabelData.user.profile_image_url || whiteLabelData.data.whiteLabel.logo_url;
      
      // Set comprehensive SEO metadata for white label forum
      this.setSeoMetadata({
        title: `${fullName}'s Chat Room - ${siteName}`,
        description: `Join ${fullName}'s chat room on ${siteName}. Connect with fans, discuss topics, and engage with the community.`,
        keywords: `${fullName}, chat room, community, ${siteName}, fan engagement, discussion forum`,
        imageUrl: logoUrl,
        canonicalUrl: currentUrl,
        ogType: 'website',
        siteName: siteName,
        locale: this.defaultLocale,
        author: fullName
      });

      // Add organization structured data for white label forum
      this.addOrganizationStructuredData({
        name: siteName,
        description: `${siteName} provides creators with their own personal branded website and app, with all their monetization offerings in a single place.`,
        logoUrl: logoUrl,
        url: currentUrl
      });

      // Set theme color and favicon for white label
      if (whiteLabelData.data.whiteLabel.bg_color) {
        this.setThemeColor(whiteLabelData.data.whiteLabel.bg_color);
      }
      // this.setFavicon(logoUrl);
      
    } else {
      // Default SEO configuration for non-white label sites
      this.setSeoMetadata({
        title: `${fullName}'s Chat Room on TopFan`,
        description: `Join ${fullName}'s chat room on TopFan. Connect with fans, discuss topics, and engage with the community.`,
        keywords: `${fullName}, chat room, TopFan, community, fan engagement, discussion forum`,
        imageUrl: userProfile?.profile_image_url || this.defaultImageUrl,
        canonicalUrl: currentUrl,
        ogType: 'website',
        siteName: this.defaultSiteName,
        locale: this.defaultLocale,
        author: fullName
      });

      // Add organization structured data for forum
      this.addOrganizationStructuredData({
        name: this.defaultSiteName,
        description: 'TopFan provides creators with their own personal branded website and app, with all their monetization offerings in a single place.',
        logoUrl: userProfile?.profile_image_url || this.defaultImageUrl,
        url: currentUrl
      });
    }
  }

  /**
   * Set SEO for leaderboard pages
   * @param creatorName - Creator's full name
   * @param userProfile - User profile data (optional)
   */
  setLeaderboardSeo(creatorName: string, userProfile?: any): void {
    const fullName = creatorName.trim();
    const currentUrl = this.getCurrentUrl();
    
    // Check if this is a white label configuration
    const whiteLabelData = extractWhiteLabelConfig();
    
    if (whiteLabelData?.data?.whiteLabel && whiteLabelData?.user) {
      // Use white label SEO configuration for leaderboard
      const siteName = whiteLabelData.data.whiteLabel.user_text || 'TopFan';
      const logoUrl = whiteLabelData.user.profile_image_url || whiteLabelData.data.whiteLabel.logo_url;
      
      // Set comprehensive SEO metadata for white label leaderboard
      this.setSeoMetadata({
        title: `${fullName}'s Leaderboard - ${siteName}`,
        description: `View ${fullName}'s top supporters and fan leaderboard on ${siteName}. See who supports this creator the most.`,
        keywords: `${fullName}, leaderboard, top supporters, fans, ${siteName}, creator support, fan engagement`,
        imageUrl: logoUrl,
        canonicalUrl: currentUrl,
        ogType: 'website',
        siteName: siteName,
        locale: this.defaultLocale,
        author: fullName
      });

      // Add organization structured data for white label leaderboard
      this.addOrganizationStructuredData({
        name: siteName,
        description: `${siteName} provides creators with their own personal branded website and app, with all their monetization offerings in a single place.`,
        logoUrl: logoUrl,
        url: currentUrl
      });

      // Set theme color and favicon for white label
      if (whiteLabelData.data.whiteLabel.bg_color) {
        this.setThemeColor(whiteLabelData.data.whiteLabel.bg_color);
      }
      // this.setFavicon(logoUrl);
      
    } else {
      // Default SEO configuration for non-white label sites
      this.setSeoMetadata({
        title: `${fullName}'s Leaderboard on TopFan`,
        description: `View ${fullName}'s top supporters and fan leaderboard on TopFan. See who supports this creator the most.`,
        keywords: `${fullName}, leaderboard, top supporters, fans, TopFan, creator support, fan engagement`,
        imageUrl: userProfile?.profile_image_url || this.defaultImageUrl,
        canonicalUrl: currentUrl,
        ogType: 'website',
        siteName: this.defaultSiteName,
        locale: this.defaultLocale,
        author: fullName
      });

      // Add organization structured data for leaderboard
      this.addOrganizationStructuredData({
        name: this.defaultSiteName,
        description: 'TopFan provides creators with their own personal branded website and app, with all their monetization offerings in a single place.',
        logoUrl: userProfile?.profile_image_url || this.defaultImageUrl,
        url: currentUrl
      });
    }
  }

  // ==================== OPTIMIZATION HELPER METHODS ====================

  /**
   * Clean text by removing HTML tags and extra whitespace
   * @param text - Text to clean
   * @returns Cleaned text
   */
  private cleanText(text: string): string {
    if (!text || typeof text !== 'string') {
      return '';
    }
    
    return text
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim(); // Remove leading/trailing whitespace
  }

  /**
   * Batch DOM updates for better performance
   * @param updateFn - Function containing DOM updates
   */
  private batchUpdate(updateFn: () => void): void {
    this.updateQueue.push(updateFn);
    
    if (!this.updateScheduled) {
      this.updateScheduled = true;
      
      // Only use requestAnimationFrame in browser environment
      if (this.platformService.isBrowser && typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(() => {
          this.updateQueue.forEach(fn => fn());
          this.updateQueue = [];
          this.updateScheduled = false;
        });
      } else {
        // In server environment, execute immediately
        this.updateQueue.forEach(fn => fn());
        this.updateQueue = [];
        this.updateScheduled = false;
      }
    }
  }

  /**
   * Get cached data or execute function and cache result
   * @param key - Cache key
   * @param fn - Function to execute if not cached
   * @param ttl - Time to live in milliseconds
   * @returns Cached or computed result
   */
  private getCached<T>(key: string, fn: () => T, ttl: number = this.CACHE_TTL): T {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < ttl) {
      return cached.data;
    }
    
    const result = fn();
    this.cache.set(key, {
      data: result,
      timestamp: Date.now()
    });
    
    return result;
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   * @returns Cache statistics
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  // /**
  //  * Updates all <link rel="apple-touch-startup-image"> href values in the document head.
  //  * Works in both browser and SSR environments
  //  * @returns Promise<void>
  //  */
  // async updateAppleTouchStartupImages(): Promise<void> {
  //   if (this.cs.isWhiteLabelWebsite() && this.platformService.isPrerendered) {
  //     try {
  //       const config = this.cs.getWhiteLabelConfiguration();
        
  //       // Safe DOM access - check if document exists
  //       if (this.document && typeof this.document.querySelectorAll === 'function') {
  //         const links = this.document.querySelectorAll('link[rel="apple-touch-startup-image"]');
  //         links.forEach((link: HTMLLinkElement, idx: number) => {
  //           link.href = this.createImageUrl(config, idx);
  //         });
  //       }
  //     } catch (error) {
  //       // Gracefully handle any errors during SSR to prevent build failure
  //       console.warn('Error updating apple touch startup images:', error);
  //     }
  //     await Promise.resolve();
  //   }
  // }

  // createImageUrl(config: any, idx: number) {
  //   let logo = config.data.whiteLabel.logo_url;
  //   const backgroundColor = config.data.whiteLabel.bg_color; // e.g. "#000000"
  
  //   // ✅ Remove domain prefix if full URL is passed
  //   if (logo.startsWith('https://')) {
  //     const parts = logo.split('.dev/'); // adjust if using .com or other env
  //     logo = parts.length > 1 ? parts[1] : logo;
  //   }
  
  //   // ✅ Convert HEX color string → RGBA object for Image Handler
  //   const hexToRgba = (hex: string) => {
  //     const c = hex.replace('#', '');
  //     const num = parseInt(c, 16);
  //     return {
  //       r: (num >> 16) & 255,
  //       g: (num >> 8) & 255,
  //       b: num & 255,
  //       alpha: 1
  //     };
  //   };
  
  //   const bg = backgroundColor ? hexToRgba(backgroundColor) : { r: 0, g: 0, b: 0, alpha: 1 };
  
  //   const imageHandlerPayload = {
  //     bucket: environment.s3Config.bucketName,
  //     key: logo,
  //     edits: {
  //       resize: {
  //         width: this.appleSplashScreens[idx].width,
  //         height: this.appleSplashScreens[idx].height,
  //         fit: 'contain',
  //         background: bg
  //       },
  //       flatten: { background: bg }
  //     }
  //   };
  
  //   // ✅ Proper UTF-8 safe encoding
  //   const base64Payload = btoa(unescape(encodeURIComponent(JSON.stringify(imageHandlerPayload))));
  //   const url = `${environment.s3Config.bucketUrl}${base64Payload}`;  
  //   return url;
  // }
}
